import React from 'react';
import { Book, Shield } from 'lucide-react';

const PrivacyPolicy = ({ onAccept, readOnly = false, onClose }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-center mb-6">
          <Shield className="w-12 h-12 text-indigo-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-800">Privacy Policy</h1>
        </div>

        <div className="space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last Updated: December 27, 2024</p>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">1. Information We Collect</h2>
            <p className="mb-3">We collect the following information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
              <li><strong>Profile Data:</strong> Country/region, subscription status, coin balance</li>
              <li><strong>Content:</strong> Book uploads, reviews, ratings, and comments</li>
              <li><strong>Usage Data:</strong> Books claimed, reviews submitted, activities performed</li>
              <li><strong>Payment Information:</strong> Processed securely through Flutterwave (we don't store card details)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain the Platform</li>
              <li>To process transactions and manage your account</li>
              <li>To match authors with reviewers</li>
              <li>To send important notifications about your account</li>
              <li>To prevent fraud and ensure platform integrity</li>
              <li>To improve our services and user experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">3. Information Sharing</h2>
            <p className="mb-3">We share your information in the following limited circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>With Other Users:</strong> Your name and reviews are visible to authors whose books you review</li>
              <li><strong>Service Providers:</strong> We use Firebase (Google) for hosting and Flutterwave for payments</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law</li>
              <li><strong>Business Transfers:</strong> In case of merger or acquisition, user data may be transferred</li>
            </ul>
            <p className="mt-3 font-semibold">We DO NOT sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">4. Data Security</h2>
            <p>We implement industry-standard security measures:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encrypted data transmission (HTTPS/SSL)</li>
              <li>Secure password hashing</li>
              <li>Firebase Authentication security</li>
              <li>Regular security audits</li>
              <li>Restricted access to personal data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">5. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct your information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="mt-3">To exercise these rights, contact us through Customer Support.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">6. Cookies and Tracking</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Analyze platform usage</li>
              <li>Improve user experience</li>
            </ul>
            <p className="mt-3">You can control cookies through your browser settings.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">7. Data Retention</h2>
            <p>We retain your data for as long as your account is active or as needed to provide services. When you delete your account, we remove your personal data within 30 days, except where required by law.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">8. Children's Privacy</h2>
            <p>Our Platform is not intended for users under 18. We do not knowingly collect information from children. If we learn we have collected data from a child, we will delete it immediately.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">9. International Data Transfers</h2>
            <p>Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">10. Changes to Privacy Policy</h2>
            <p>We may update this Privacy Policy periodically. We will notify you of significant changes via email or platform notification. Continued use after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 mb-3">11. Contact Us</h2>
            <p>For privacy-related questions or to exercise your rights, contact us through the Customer Support feature in the app.</p>
          </section>
        </div>

         {/* ✅ UPDATED: Different buttons based on readOnly mode */}
        <div className="mt-8">
          
          <button
            onClick={readOnly ? onClose : onAccept}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            {readOnly ? 'Close' : 'I Understand'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;